import { useEffect, useState } from "react";
import { useDemo } from "@/contexts/DemoContext";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Trophy, ShoppingBag, ArrowRight, PartyPopper } from "lucide-react";

const CELEBRATION_KEY = "chawni-demo-celebration-seen";

export const DemoCompletionCelebration = () => {
  const { completedSteps, totalSteps } = useDemo();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [hasSeen, setHasSeen] = useState(true);

  useEffect(() => {
    const seen = localStorage.getItem(CELEBRATION_KEY);
    setHasSeen(!!seen);
  }, []);

  useEffect(() => {
    if (completedSteps === totalSteps && !hasSeen) {
      const timer = setTimeout(() => setOpen(true), 500);
      return () => clearTimeout(timer);
    }
  }, [completedSteps, totalSteps, hasSeen]);

  const handleClose = () => {
    localStorage.setItem(CELEBRATION_KEY, "true");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-sm p-0 overflow-hidden border-none bg-transparent shadow-none [&>button]:hidden">
        <div className="rounded-2xl overflow-hidden bg-card border border-border shadow-2xl text-center">
          {/* Confetti-like top decoration */}
          <div className="h-2 bg-gradient-to-r from-emerald-500 via-violet-500 to-amber-500" />

          <div className="px-8 py-8">
            {/* Animated trophy */}
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.2 }}
              className="flex justify-center mb-6"
            >
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-xl shadow-amber-500/30">
                <Trophy className="w-12 h-12 text-white" />
              </div>
            </motion.div>

            {/* Emoji burst */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex justify-center gap-2 mb-4 text-2xl"
            >
              <PartyPopper className="w-6 h-6 text-amber-500" />
              <span>🎉</span>
              <PartyPopper className="w-6 h-6 text-violet-500" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h3 className="text-2xl font-bold text-foreground mb-2 font-display">
                Demo Complete!
              </h3>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                You've explored all the key features of Chawni Phone.
                Ready to experience the real thing?
              </p>

              {/* Achievement badges */}
              <div className="grid grid-cols-2 gap-2 mb-6">
                {[
                  { emoji: "👀", label: "Browsed phones" },
                  { emoji: "🛒", label: "Made a purchase" },
                  { emoji: "🛡️", label: "Viewed passport" },
                  { emoji: "🏪", label: "Listed on marketplace" },
                ].map(({ emoji, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-2 p-2 rounded-lg bg-emerald-500/10 text-xs font-medium text-emerald-700 dark:text-emerald-400"
                  >
                    <span>{emoji}</span>
                    <span>{label}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="space-y-2">
                <Button
                  className="w-full gap-2"
                  onClick={() => {
                    handleClose();
                    navigate("/shop");
                  }}
                >
                  <ShoppingBag className="w-4 h-4" />
                  Start Shopping for Real
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <p className="text-xs text-muted-foreground">
                  🎁 Get <span className="font-semibold text-primary">10% off</span> your first real purchase!
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
